// src/common/core/hooks/use-region-init.ts
import { watch, shallowRef } from 'vue'
import { useRoute } from 'vue-router'
import {
  baseInfo,
  kFallbackRegion,
  emitBaseInfoChanged,
  normalizeRegion,
  type Region,
} from '@kce2/common/core/baseinfo'
import { getKsStorage, type RegionControl } from '@kce2/common/utils/ksStorage'
import { queryClient } from '@kce2/common/api/tanstack'
import { getRegions } from '@kce2/common/api/commons'
import { send } from '@kce2/common/core/request/request'
import type { RouteMeta } from 'vue-router'
import type { RegionListSource } from '@kce2/app/types/region'

/**
 * Check if current route is an entry page (shows region selector)
 * Default is false (sub-page) for safety
 */
function isEntryPage(meta: RouteMeta | undefined): boolean {
  return meta?.showRegionList === true
}

/**
 * Resolve region for sub-pages
 * Priority: path param > query param ?region > query param ?r > fallback
 */
function resolveSubPageRegion(route: ReturnType<typeof useRoute>): string {
  const pathRegion = route.params.region as string | undefined
  if (pathRegion) return pathRegion

  const qRegion = route.query.region as string | undefined
  if (qRegion) return qRegion

  const qRegionShort = route.query.r as string | undefined
  if (qRegionShort) return qRegionShort

  return kFallbackRegion
}

/**
 * Resolve region list from RegionListSource
 */
async function resolveRegionList(source: RegionListSource | undefined): Promise<Region[]> {
  if (source === false) {
    return []
  }
  if (source === undefined || source === true) {
    // Default fetch using existing API
    const res = await queryClient.fetchQuery({
      queryKey: ['DescribeRegions'],
      queryFn: async () => send(getRegions()),
    })

    const regions = ('Regions' in res.data.Data ? res.data.Data.Regions : res.data.Data) || []
    return regions.map(normalizeRegion)
  }
  if (Array.isArray(source)) {
    return source
  }
  if (typeof source === 'function') {
    return source()
  }
  return []
}

async function initEntryPage(
  route: ReturnType<typeof useRoute>,
  meta: RouteMeta | undefined,
  regionControl: { value: RegionControl | null },
  onRegionChange: (region: string) => void
) {
  // Destroy previous (removes listener too)
  regionControl.value?.remove()

  // Parallel: fetch regions + get stored region
  const [regions, regionFromKsStorage] = await Promise.all([
    resolveRegionList(meta?.regionList),
    new Promise<string | undefined>(resolve => {
      // getValue is not in the ksc-types declaration but exists on __ksStorage__
      ;(getKsStorage() as any).getValue('region', (_: string, value: string) => resolve(value || undefined))
    }),
  ])

  // Resolve initial region
  const qRegionShort = route.query.r as string | undefined
  const qRegion = route.query.region as string | undefined
  const initialRegion = qRegionShort ?? qRegion ?? regionFromKsStorage ?? kFallbackRegion

  // Update baseInfo (regionFull auto-derived by watch)
  baseInfo.regions = regions
  baseInfo.region = initialRegion

  // Map regions for ksStorage format
  const ksRegions = regions.map(v => ({
    regionId: v.RegionId,
    regionCode: v.RegionCode,
    regionName: v.RegionName,
    regionEnName: v.RegionEnName,
    areaCode: v.AreaCode,
    areaName: v.AreaName,
    areaEnName: v.AreaEnName,
  }))

  // Initialize region list in header
  regionControl.value = getKsStorage().initRegion(ksRegions, initialRegion)

  // Setup listener AFTER initRegion (every time, as listener is tied to region list lifecycle)
  getKsStorage().on('region', onRegionChange)
}

function initSubPage(
  route: ReturnType<typeof useRoute>,
  meta: RouteMeta | undefined,
  regionControl: { value: RegionControl | null }
) {
  // Destroy previous (removes listener too)
  regionControl.value?.remove()
  regionControl.value = null

  // Set region from URL immediately (non-blocking)
  baseInfo.region = resolveSubPageRegion(route)

  // Optionally fetch regions async for regionFull lookup
  const needList = meta?.regionList ?? true // default to fetch
  if (needList) {
    resolveRegionList(meta?.regionList).then(regions => {
      baseInfo.regions = regions
      // regionFull auto-derived by watch
    })
  }
}

export function useRegionInit() {
  const route = useRoute()
  const regionControl = shallowRef<RegionControl | null>(null)

  function onRegionChange(region: string) {
    baseInfo.region = region
    // regionFull auto-derived by watch in baseinfo.ts
    emitBaseInfoChanged()
  }

  async function handleRouteChange(meta: RouteMeta | undefined, _prevMeta: RouteMeta | undefined) {
    baseInfo.regionReady = false
    const showList = isEntryPage(meta)

    if (showList) {
      await initEntryPage(route, meta, regionControl, onRegionChange)
    } else {
      initSubPage(route, meta, regionControl)
    }
    baseInfo.regionReady = true
  }

  // Auto-watch route changes
  watch(() => route.meta, (meta, prevMeta) => {
    handleRouteChange(meta, prevMeta)
  })

  return {
    init: () => handleRouteChange(route.meta, undefined),
    regionControl,
  }
}
